import React from 'react'
import cl from 'classnames'
import { observer } from 'mobx-react'

import { Icon } from 'Shared/atoms'
import { IconType } from 'Shared/atoms/Icon'
import useBasicStores from 'Shared/hooks/useBasicStores'
import { Button } from 'Shared/molecules'
import { BtnType } from 'Shared/molecules/Button/Button'
import Modal from 'Shared/organisms/Modal/Modal'

import styles from './styles.module.scss'

const { ModalBody, ModalFooter } = Modal

export interface Props {
	onClose: () => void
	handleConfirm: () => void
	handleSecondConfirm?: () => void
	title: string
	subtitle?: string
	okBtnText?: string
	okSecondBtnText?: string
	cancelBtnText?: string
	btnType?: BtnType
	iconType?: IconType
	hideCancelBtn?: boolean
	classNameFooter?: string
	classNameSubtitleText?: string
	loading?: boolean
	danger?: boolean
	disabled?: boolean
	bodyAlignment?: 'left' | 'center' | 'right'
	closeBeforeConfirm?: boolean
	modalBodyClassName?: string
	shouldCloseOnConfirm?: boolean
	children?: React.ReactNode
}

const WarningModal: React.FC<Props> = ({
	handleConfirm,
	handleSecondConfirm,
	onClose,
	title,
	subtitle,
	okBtnText = 'Delete',
	okSecondBtnText,
	cancelBtnText = 'Cancel',
	btnType = 'danger',
	iconType = 'warning',
	hideCancelBtn = false,
	classNameFooter,
	classNameSubtitleText,
	children,
	danger = true,
	loading = false,
	disabled,
	bodyAlignment = 'center',
	closeBeforeConfirm = false,
	modalBodyClassName = '',
	shouldCloseOnConfirm = true
}) => {
	const {
		uiStore: { isRequestSubmitting }
	} = useBasicStores()

	const handleOK = async () => {
		if (closeBeforeConfirm) {
			shouldCloseOnConfirm && onClose()
			await handleConfirm()
		} else {
			await handleConfirm()
			shouldCloseOnConfirm && onClose()
		}
	}

	const handleSecondOK = async () => {
		if (closeBeforeConfirm) {
			shouldCloseOnConfirm && onClose()
			await handleSecondConfirm?.()
		} else {
			await handleSecondConfirm?.()
			shouldCloseOnConfirm && onClose()
		}
	}

	return (
		<>
			<ModalBody className={cl(modalBodyClassName, styles.modalWarningBody)}>
				<div className={cl(styles.warningInfo, styles[bodyAlignment])}>
					<Icon className={styles.warningIcon} type={iconType} />
					<p className={styles.title}>{title}</p>
					{subtitle ? (
						<span className={cl(styles.subtitle, classNameSubtitleText)}>
							{subtitle}
						</span>
					) : null}
					{children}
				</div>
			</ModalBody>
			<ModalFooter className={classNameFooter}>
				{!hideCancelBtn && (
					<Button disabled={loading} btnType="secondary" size="small" onClick={onClose}>
						{cancelBtnText}
					</Button>
				)}
				{okSecondBtnText && (
					<Button
						className={styles.continueBtn}
						btnType="secondary"
						size="small"
						danger={danger}
						loading={loading}
						onClick={handleSecondOK}
					>
						{okSecondBtnText}
					</Button>
				)}
				<Button
					className={styles.continueBtn}
					btnType={btnType}
					size="small"
					danger={danger}
					loading={isRequestSubmitting}
					onClick={handleOK}
					disabled={disabled}
				>
					{okBtnText}
				</Button>
			</ModalFooter>
		</>
	)
}

export default observer(WarningModal)
